import { useState } from "react";
import { FileTypeIcon } from "./file-type-icon";
import type { DocumentFile } from "@/types/documents";
import { cx } from "@/utils/cx";
import { Checkbox } from "@/components/base/checkbox/checkbox";

interface DocumentCardProps {
    document: DocumentFile;
    onClick?: () => void;
    onContextMenu?: (e: React.MouseEvent) => void;
    isSelected?: boolean;
    viewMode?: "card" | "list";
    className?: string;
    onCheckboxChange?: (checked: boolean) => void;
    showCheckbox?: boolean;
}

export const DocumentCard = ({
    document,
    onClick,
    onContextMenu,
    isSelected = false,
    viewMode = "card",
    className,
    onCheckboxChange,
    showCheckbox = false,
}: DocumentCardProps) => {
    const [isHovered, setIsHovered] = useState(false);
    const shouldShowCheckbox = showCheckbox && (isHovered || isSelected);

    if (viewMode === "list") {
        return (
            <div
                className={cx(
                    "flex items-center gap-3 rounded-lg px-4 py-3 cursor-pointer transition-all relative",
                    "hover:bg-primary_hover focus-visible:outline-2 focus-visible:outline-offset-2",
                    isSelected && "bg-active ring-2 ring-brand",
                    className
                )}
                onClick={onClick}
                onContextMenu={onContextMenu}
                onMouseEnter={() => setIsHovered(true)}
                onMouseLeave={() => setIsHovered(false)}
                role="button"
                tabIndex={0}
            >
                {shouldShowCheckbox && onCheckboxChange && (
                    <div
                        className="absolute left-2 top-1/2 -translate-y-1/2 z-10"
                        onClick={(e) => e.stopPropagation()}
                    >
                        <Checkbox
                            isSelected={isSelected}
                            onChange={(checked) => onCheckboxChange(checked)}
                        />
                    </div>
                )}
                <FileTypeIcon type={document.type} fileName={document.name} size="md" />
                <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-primary truncate">{document.name}</p>
                    <p className="text-xs text-tertiary capitalize">{document.type.replace(/_/g, " ")}</p>
                </div>
                <div className="text-xs text-tertiary">
                    {new Date(document.uploadedAt).toLocaleDateString()}
                </div>
            </div>
        );
    }

    return (
        <div
            className={cx(
                "rounded-xl bg-primary p-4 shadow-xs ring-1 ring-secondary cursor-pointer transition-all relative",
                "hover:ring-2 hover:shadow-sm focus-visible:outline-2 focus-visible:outline-offset-2",
                isSelected && "ring-2 ring-brand shadow-md",
                className
            )}
            onClick={onClick}
            onContextMenu={onContextMenu}
            onMouseEnter={() => setIsHovered(true)}
            onMouseLeave={() => setIsHovered(false)}
            role="button"
            tabIndex={0}
        >
            {shouldShowCheckbox && onCheckboxChange && (
                <div
                    className="absolute top-2 left-2 z-10"
                    onClick={(e) => e.stopPropagation()}
                >
                    <Checkbox
                        isSelected={isSelected}
                        onChange={(checked) => onCheckboxChange(checked)}
                    />
                </div>
            )}
            <div className="flex flex-col items-center text-center gap-3">
                <FileTypeIcon type={document.type} fileName={document.name} size="lg" className="text-brand-600" />
                <div className="w-full min-w-0">
                    <p className="text-sm font-medium text-primary truncate" title={document.name}>
                        {document.name}
                    </p>
                    <p className="text-xs text-tertiary mt-1 capitalize">
                        {document.type.replace(/_/g, " ")}
                    </p>
                </div>
            </div>
        </div>
    );
};

